import {useEffect, useState} from 'react'
import {useLayout} from '../../../_metronic/layout/core'
import {Warehouse, createWarehouse, editWarehouse} from './api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'

// ─── Validation Schema ────────────────────────────────────────────────────────

const warehouseSchema = Yup.object().shape({
  warehouse_name: Yup.string()
    .required('Warehouse name is required')
    .matches(/^[a-zA-Z0-9\-' ]{1,100}$/, {
      message:
        'Warehouse name must be 1-100 characters and can only contain letters, numbers, hyphens, apostrophes and spaces',
    }),
  street_address: Yup.string().required('Street address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  country: Yup.string().required('Country is required'),
  zip_code: Yup.string().required('Zip code is required'),
  contact_person: Yup.string().required('Contact person is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phone_number: Yup.string().required('Phone number is required'),
  warehouse_type: Yup.string()
    .oneOf(['Central', 'Branch', 'Distribution', 'Retail'])
    .required('Warehouse type is required'),
  status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface WarehouseFormValues {
  warehouse_name: string
  street_address: string
  city: string
  state: string
  country: string
  zip_code: string
  contact_person: string
  email: string
  phone_number: string
  warehouse_type: string
  status: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateEditWarehouse: React.FC<{
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>
  setEditWarehouse: React.Dispatch<React.SetStateAction<Warehouse | undefined>>
  editWarehouseData?: Warehouse
  setShouldRefreshWarehouses: React.Dispatch<React.SetStateAction<boolean>>
}> = ({setShowForm, editWarehouseData, setShouldRefreshWarehouses, setEditWarehouse}) => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)

  const initialValues: WarehouseFormValues = {
    warehouse_name: editWarehouseData?.warehouse_name || '',
    street_address: editWarehouseData?.street_address || '',
    city: editWarehouseData?.city || '',
    state: editWarehouseData?.state || '',
    country: editWarehouseData?.country || '',
    zip_code: editWarehouseData?.zip_code || '',
    contact_person: editWarehouseData?.contact_person || '',
    email: editWarehouseData?.email || '',
    phone_number: editWarehouseData?.phone_number || '',
    warehouse_type: editWarehouseData?.warehouse_type || 'Central',
    status: editWarehouseData?.status || 'active',
  }

  const formik = useFormik<WarehouseFormValues>({
    initialValues,
    validationSchema: warehouseSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setLoading(true)
      const body = {
        warehouse_id: editWarehouseData?.warehouse_id || null,
        company_id: company?.value,
        warehouse_name: values.warehouse_name,
        street_address: values.street_address,
        city: values.city,
        state: values.state,
        country: values.country,
        zip_code: values.zip_code,
        contact_person: values.contact_person,
        email: values.email,
        phone_number: values.phone_number,
        warehouse_type: values.warehouse_type,
        status: values.status,
      }

      if (!editWarehouseData?.warehouse_id) {
        await createWarehouse(body)
          .then((res) => {
            res?.detail && toast.success('Warehouse has been added successfully.')
            formik.resetForm()
            setShouldRefreshWarehouses(true)
          })
          .catch((err) => toast.error(err?.detail || err.message))
          .finally(() => {
            setLoading(false)
            setShowForm(false)
          })
      } else {
        await editWarehouse(body)
          .then((res) => {
            res?.detail && toast.success('Warehouse has been updated successfully.')
            formik.resetForm()
            setShouldRefreshWarehouses(true)
          })
          .catch((err) => toast.error(err?.detail || err.message))
          .finally(() => {
            setLoading(false)
            setShowForm(false)
          })
      }
    },
  })

  return (
    <div className='card'>
      <div
        className='card-header border-0'
        data-bs-target='#kt_warehouse_form'
        aria-expanded='true'
        aria-controls='kt_warehouse_form'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>
            {!!editWarehouseData ? 'Update Warehouse' : 'Add New Warehouse'}
          </h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div
          id='kt_warehouse_form'
          className='collapse show d-flex justify-content-center flex-column flex-md-row'
        >
          {/* Left Column */}
          <div className='p-8 col pb-0 pb-md-4'>
            {/* Warehouse Name */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                Warehouse Name
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Warehouse Name'
                  {...formik.getFieldProps('warehouse_name')}
                />
                {formik.touched.warehouse_name && formik.errors.warehouse_name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.warehouse_name}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Street Address */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                Street Address
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Street Address'
                  {...formik.getFieldProps('street_address')}
                />
                {formik.touched.street_address && formik.errors.street_address && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.street_address}</div>
                  </div>
                )}
              </div>
            </div>

            {/* City */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>City</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='City'
                  {...formik.getFieldProps('city')}
                />
                {formik.touched.city && formik.errors.city && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.city}</div>
                  </div>
                )}
              </div>
            </div>

            {/* State */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>State</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='State'
                  {...formik.getFieldProps('state')}
                />
                {formik.touched.state && formik.errors.state && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.state}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Country */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Country</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Country'
                  {...formik.getFieldProps('country')}
                />
                {formik.touched.country && formik.errors.country && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.country}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Zip Code */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Zip Code</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Zip Code'
                  {...formik.getFieldProps('zip_code')}
                />
                {formik.touched.zip_code && formik.errors.zip_code && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.zip_code}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className='p-8 col pt-0 pt-md-8 pb-md-4'>
            {/* Contact Person */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                Contact Person
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Contact Person'
                  {...formik.getFieldProps('contact_person')}
                />
                {formik.touched.contact_person && formik.errors.contact_person && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.contact_person}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Email</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='email'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Email'
                  {...formik.getFieldProps('email')}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.email}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Phone Number */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Phone Number</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Phone Number'
                  {...formik.getFieldProps('phone_number')}
                />
                {formik.touched.phone_number && formik.errors.phone_number && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.phone_number}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Warehouse Type */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Type</label>
              <div className='col-lg-9 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg'
                  {...formik.getFieldProps('warehouse_type')}
                >
                  <option value='Central'>Central</option>
                  <option value='Branch'>Branch</option>
                  <option value='Distribution'>Distribution</option>
                  <option value='Retail'>Retail</option>
                </select>
                {formik.touched.warehouse_type && formik.errors.warehouse_type && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.warehouse_type}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Status</label>
              <div className='col-lg-9 fv-row d-flex'>
                <span className='form-check form-check-custom form-check-solid fs-6'>
                  Active{' '}
                  <input
                    className='form-check-input'
                    type='radio'
                    name='status'
                    value='active'
                    checked={formik.values.status === 'active'}
                    onChange={() => formik.setFieldValue('status', 'active')}
                    style={{marginLeft: '10px'}}
                  />
                </span>
                <span
                  style={{marginLeft: '20px'}}
                  className='form-check form-check-custom form-check-solid fs-6'
                >
                  Inactive{' '}
                  <input
                    className='form-check-input'
                    type='radio'
                    name='status'
                    value='inactive'
                    checked={formik.values.status === 'inactive'}
                    onChange={() => formik.setFieldValue('status', 'inactive')}
                    style={{marginLeft: '10px'}}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-9 border-0 pb-7 pt-0'>
          <button
            type='button'
            className='btn btn-secondary'
            style={{marginRight: '10px'}}
            onClick={() => {
              formik.resetForm()
              setEditWarehouse(undefined)
              setShowForm(false)
              setShouldRefreshWarehouses(false)
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary ml-2'
            disabled={loading || !formik.isValid}
          >
            {!loading && !!editWarehouseData ? 'Save Warehouse' : 'Add Warehouse'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
